import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  combineLatest,
  first,
  map,
  mergeMap,
  Observable,
  of,
  retry,
  Subscription,
  switchMap,
  tap,
  throwError
} from 'rxjs';
import {
  AdditionalPageShort,
  AuthCodeResponse,
  AuthCodeResponseGenericResponse,
  CustomerProfileDto,
  LoyaltyProvider
} from 'src/app/api/v1/models';
import { AuthService } from 'src/app/pages/auth/auth.service';
import { ModalSwipeDirective } from 'src/app/shared/directives/modal-swipe/modal-swipe.directive';
import { ConfigRepository } from 'src/app/shared/repositories/config.repository';
import { CustomerRepository } from 'src/app/shared/repositories/customer.repository';
import { BreakpointService } from 'src/app/shared/services/breakpoint.service';
import { RegisterData } from '../fresh/fresh-auth-page/register-form.interface';

enum AuthState {
  PhoneInput,
  RegisterState,
  CodeInput,
}

@UntilDestroy()
@Directive()
export abstract class AuthPageAbstract implements OnInit, OnDestroy {
  private formControls: FormArray;
  private timerInterval: ReturnType<typeof setInterval>;
  private timeToRetry: number = 45;

  @Output() getMaskedPhone = new EventEmitter<string>();
  @Output() closeModal = new EventEmitter();
  @ViewChild(ModalSwipeDirective) modalSwipe: ModalSwipeDirective;

  loyaltyProviders = LoyaltyProvider;

  additionalPrivacy$: Observable<AdditionalPageShort | undefined> = this.configRepository.additionalPrivacy$;
  loyaltyAuthProvider$: Observable<LoyaltyProvider> = this.configRepository.loyaltyAuthProvider$;
  isIiko$: Observable<boolean | null> = this.configRepository.isIiko$;
  fromRussia$: Observable<boolean> = this.configRepository.fromRussia$;
  hasPrivacy$: Observable<boolean> =
    combineLatest([
      this.configRepository.personalDataPolicyPage$,
      this.configRepository.personalDataConsentPage$,
    ]).pipe(
      map(
        ([personalDataPolicyPage, personalDataConsentPage]) => !!personalDataPolicyPage && !!personalDataConsentPage
      )
    )

  phoneCtrl: FormControl<string> = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  registerCtrl: FormControl<RegisterData | null> = new FormControl<RegisterData | null>(null);
  codeCtrl: FormControl<string | null> = new FormControl(null);

  isLoading: boolean = false;
  isLockSubmit: boolean = false;
  isCodeSubmit: boolean = false;
  authState: AuthState = AuthState.PhoneInput;
  AuthStateType = AuthState;
  isCodeError: boolean = false;
  currentTime: number = this.timeToRetry;
  codeMessage: string | null = null;
  registerErrorMessage: string | null = null;
  hiddenEmail: string | null = null;
  hiddenPhone: string | null = null;
  clearCode: boolean = false;
  phoneErrorMessage: string | null = null;

  constructor(
    private customerRepository: CustomerRepository,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private renderer: Renderer2,
    public breakPoint: BreakpointService,
    private configRepository: ConfigRepository,
  ) { }

  ngOnInit(): void {
    this.formControls = new FormArray([
      this.phoneCtrl,
      this.codeCtrl,
      this.registerCtrl
    ]);
    this.renderer.addClass(document.documentElement, 'is-scroll');
  }

  onClose(): void {
    this.renderer.removeClass(document.documentElement, 'is-scroll');
    this.closeModal.emit();
  }

  closeMobileModal(): void {
    this.modalSwipe.hideModal();
  }

  onKeydownPhone(event: KeyboardEvent): void {
    if (!event.shiftKey && (event.key === 'Enter')) {
      this.onSubmit();
    }
  }


  onSubmit(): void {
    if (!this.phoneCtrl.valid) {
      this.phoneCtrl.updateValueAndValidity();
      this.cdr.detectChanges();
      return;
    }

    if (this.isLoading) { return }
    this.isLoading = true;

    const phone = this.phoneCtrl.value as string;
    this.phoneErrorMessage = null;

    this.customerRepository.requestAuthCode(phone).pipe(
      untilDestroyed(this),
      mergeMap(authCodeResponse =>
        authCodeResponse.isRegistered !== authCodeResponse.isSent ? throwError(() => 'error') : of(authCodeResponse)
      ),
      retry({ delay: 1000, count: 4 }),
    ).subscribe({
      next: (authCodeResponse: AuthCodeResponse) => {
        this.isLoading = false;

        if (!authCodeResponse.isRegistered) {
          this.registerCtrl.patchValue({
            phone: this.phoneCtrl.getRawValue(),
            email: ''
          })
          this.authState = AuthState.RegisterState;
          this.cdr.detectChanges();
          return;
        }

        if (authCodeResponse.hiddenEmail) {
          this.hiddenEmail = authCodeResponse.hiddenEmail;
          this.hiddenPhone = authCodeResponse.hiddenPhone || null;
        }

        this.setCodeState();
      },
      error: () => {
        this.isLoading = false;
        this.phoneErrorMessage = $localize`:@@FailedToAuth:Failed to log in. Try again later`;
      }
    });
  }

  private setCodeState() {
    this.authState = AuthState.CodeInput;
    this.isLockSubmit = true;

    this.timerInterval = setInterval(() => {
      this.currentTime--;
      if (this.currentTime == 0) {
        clearInterval(this.timerInterval);
        this.currentTime = this.timeToRetry;
        this.isLockSubmit = false;
      }

    }, 1000);
    this.cdr.detectChanges();
  }

  onRegister(): void {
    if (!this.registerCtrl.valid) {
      this.registerCtrl.updateValueAndValidity();
      this.cdr.detectChanges();
      return;
    }

    if (this.isLoading) { return }
    this.isLoading = true;

    const formValue = this.registerCtrl.getRawValue();

    if (!formValue) { return }

    this.customerRepository.register(formValue).pipe(
      untilDestroyed(this)
    ).subscribe((authCodeResponse: AuthCodeResponseGenericResponse) => {
      this.isLoading = false;
      if (authCodeResponse.error !== null) {
        this.registerErrorMessage = $localize`:@@FailedToRegister:Failed to register. Try again later`;
        this.registerCtrl.updateValueAndValidity();
        this.cdr.detectChanges();
        return;
      }

      if (authCodeResponse.result?.hiddenEmail) {
        this.hiddenEmail = authCodeResponse.result.hiddenEmail;
        this.hiddenPhone = authCodeResponse.result.hiddenPhone || null;
      }

      this.setCodeState();
      this.cdr.detectChanges();
    });
  }

  onBackToPhone(): void {
    this.authState = AuthState.PhoneInput;
    this.hiddenEmail = null;
    this.hiddenPhone = null;

    this.codeMessage = null;
  }

  private loginSub: Subscription;
  onCompleteCode(): void {
    if (this.isCodeSubmit) { return }

    this.isCodeSubmit = true;
    this.codeCtrl.disable();

    const code = this.codeCtrl.value as string;
    this.loginSub = this.customerRepository.requestProfile(code).pipe(
      map((value: CustomerProfileDto | null) => {
        this.isCodeSubmit = false;
        if (value) {
          this.resetModalState();
        } else {
          this.isCodeError = true;
          this.codeCtrl.enable();
          setTimeout(() => {
            this.isCodeError = false;
            this.codeMessage = $localize`:@@IncorrectCode:Incorrect code`;
          }, 400);

          this.clearCode = true;
        }

        this.cdr.detectChanges();

        return !!value;
      }),
      switchMap(isValue => isValue ? this.authService.isRedirectToPersonalPage$ : of(false)),
      first(),
      tap(isRedirect => {
        if (!isRedirect) { return }
        void this.router.navigateByUrl('/customer-area');
      }),
    ).subscribe({
      error: () => {
        this.resetModalState();
        this.loginSub?.unsubscribe();
      }
    });
  }

  private resetModalState(): void {
    this.codeCtrl.enable();
    this.formControls.controls.forEach((f: AbstractControl<unknown>) => f.reset());
    this.hiddenEmail = null;
    this.hiddenPhone = null;
    clearInterval(this.timerInterval);
    this.currentTime = this.timeToRetry;

    this.codeMessage = null;

    this.isLockSubmit = false;
    this.authState = AuthState.PhoneInput;
    this.closeModal.emit();
    this.cdr.detectChanges();
  }

  getPhone(mask: string): void {
    this.getMaskedPhone.emit(mask);
  }

  authCdeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return this.registerErrorMessage !== null ? { registerError: { value: control.value } } : null;
    };
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.documentElement, 'is-scroll');
  }
}
